import { isAxiosError } from 'axios';
import posthog from 'posthog-js';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { captureError } from '@/shared/lib/errors/capture';

type WhatsNewOp = 'fetch' | 'mark-read';

const FAILED_EVENT: Record<WhatsNewOp, string> = {
  fetch: AnalyticsEvent.WHATS_NEW_FETCH_FAILED,
  'mark-read': AnalyticsEvent.WHATS_NEW_MARK_READ_FAILED,
};

type StatusClass = '4xx' | '5xx' | 'no-status';

function statusClassOf(status: number | undefined): StatusClass {
  if (status === undefined) return 'no-status';
  return status < 500 ? '4xx' : '5xx';
}

/**
 * 401 is already reconciled by the base client (logout), 403 only happens with a
 * pending 2FA challenge, and 429 is the BE's 30/min limiter — all three are
 * expected and kept as noise. Status-class fingerprint so a real 5xx gets its
 * own Sentry issue instead of hiding under the 4xx one.
 */
export function reportWhatsNewError(error: unknown, op: WhatsNewOp): void {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 401) return;
  const statusClass = statusClassOf(status);
  const expected = status === 403 || status === 429;

  posthog.capture(FAILED_EVENT[op], {
    error_type: statusClass,
    error_code: status === undefined ? 'network' : `http_${status}`,
  });
  captureError(error, {
    component: 'WhatsNew',
    action: op,
    fingerprint: ['whats-new', op, statusClass],
    ...(expected ? { severity: 'warning', tags: { signal: 'noise' } } : {}),
    ...(status !== undefined ? { extra: { http_status: status } } : {}),
  });
}
